import React, { useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { motion } from 'framer-motion';
import SeverityBadge from '@/components/apex/SeverityBadge';
import ActionMenu from '@/components/apex/ActionMenu';
import AnimatedCounter from '@/components/apex/AnimatedCounter';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertTriangle, Activity } from 'lucide-react';
import HowCalculated from '@/components/apex/HowCalculated';
import InvestigationBadge from '@/components/apex/InvestigationBadge';
import EdgeEvidenceDialog from '@/components/apex/EdgeEvidenceDialog';
import ExposureScrubber from '@/components/apex/ExposureScrubber';
import BlastRadiusGraph from '@/components/apex/BlastRadiusGraph';
import RecordDrawer from '@/components/apex/RecordDrawer';
import useListDrawer from '@/hooks/useListDrawer';

export default function EnvironmentIntegrity() {
  const { seed } = useApp();
  const navigate = useNavigate();
  const [activeEdge, setActiveEdge] = useState(null);
  const findings = seed?.environment_findings ?? [];
  const {
    active: activeFinding,
    setActive: setActiveFinding,
    openPrev: findingPrev,
    openNext: findingNext,
    index: findingIndex,
    total: findingTotal,
  } = useListDrawer({ items: findings });
  if (!seed) return null;

  const edges = seed.contamination_edges ?? [];

  const buildFindingRecord = (f) => {
    const overSla = f.duration_days - f.sla_days;
    const sevTone = f.severity === 'critical' ? 'critical' : f.severity === 'high' ? 'warning' : 'neutral';
    return {
      idTag: f.id,
      title: f.title,
      severity: f.severity,
      subtitle: 'Environment integrity exposure · correlated from CloudTrail, AWS Config, and IAM events',
      verdict: {
        tone: sevTone,
        label: overSla > 0 ? `${overSla} days past SLA` : `Inside SLA window`,
        sub: overSla > 0
          ? `This crossing has been live ${f.duration_days} days versus a ${f.sla_days}-day remediation target. Auditors will read it as an unmitigated control gap.`
          : `Detected ${f.duration_days} days ago and tracking under the ${f.sla_days}-day remediation budget.`,
      },
      signal: {
        tone: sevTone,
        lead: 'Why APEX surfaced this:',
        body: 'A production resource is operating with a configuration, artifact, or trust path that did not originate from an approved non-production lineage. Until the path is severed or re-approved, every upstream change is a downstream exposure.',
      },
      meta: [
        { label: 'Time in production', value: `${f.duration_days}d`, mono: true, tone: overSla > 0 ? 'critical' : 'success', sub: overSla > 0 ? `${overSla}d past SLA` : 'Within remediation budget', delta: overSla > 0 ? 'up' : 'flat' },
        { label: 'Remediation SLA', value: `${f.sla_days}d`, mono: true, sub: 'Per Stratum policy CG-04' },
        { label: 'Data in scope', value: f.data_impact, sub: 'Sensitivity classification' },
        { label: 'Accountable team', value: f.remediation_owner, sub: 'Assigned owner of record' },
        { label: 'Linked investigation', value: f.investigation ?? 'Not linked', mono: true, tone: f.investigation ? 'critical' : '', sub: f.investigation ? 'Active CISO inquiry · click to open' : 'No open inquiry', pivot: f.investigation ? { to: `/lens/investigations?id=${f.investigation}`, label: `Open ${f.investigation}` } : undefined },
      ],
      sections: [
        {
          title: 'Contamination path',
          type: 'chart',
          chart: {
            kind: 'progress',
            steps: [
              { label: 'DEV ACCOUNT',      sub: 'stratum-dev-sandbox · loosely governed',                          state: 'done' },
              { label: 'Shared IAM role',  sub: 'developer-role · created Oct 3, 2025 · no baseline',              state: 'done' },
              { label: 'Trust traversal', sub: 'Apr 14, 2026 02:17 UTC · 2 successful AssumeRole',                 state: 'done' },
              { label: 'PROD ACCOUNT',    sub: 'stratum-payments-prod · PCI scope',                               state: 'done' },
              { label: 'PCI data accessed', sub: 'stratum-pci-customer-data · 3 GetObject events',                state: 'active' },
            ],
          },
        },
        {
          title: 'Exposure window vs. SLA',
          icon: Activity,
          type: 'chart',
          chart: {
            kind: 'area',
            label: 'Days the path has been live',
            sublabel: `Remediation budget · ${f.sla_days} day${f.sla_days === 1 ? '' : 's'}`,
            labels: ['W-4', 'W-3', 'W-2', 'W-1', 'Now'],
            values: [
              Math.max(0, f.duration_days - 28),
              Math.max(0, f.duration_days - 21),
              Math.max(0, f.duration_days - 14),
              Math.max(0, f.duration_days - 7),
              f.duration_days,
            ],
            threshold: f.sla_days,
            accent: overSla > 0 ? '#DC2626' : '#0EA5E9',
          },
        },
        {
          title: 'Operational signals',
          type: 'kpis',
          kpis: [
            { label: 'Time-to-remediate', value: overSla > 0 ? `+${overSla}d` : 'On track', sub: overSla > 0 ? 'Trending against the SLA budget' : 'Pacing inside the remediation window', tone: overSla > 0 ? 'critical' : 'success', delta: overSla > 0 ? 'up' : 'down' },
            { label: 'Blast radius', value: f.severity === 'critical' ? 'Tier 0' : f.severity === 'high' ? 'Tier 1' : 'Tier 2', sub: 'Resources reachable from this path', tone: f.severity === 'critical' ? 'critical' : 'warning', delta: 'flat' },
            { label: 'Audit weight', value: f.investigation ? 'Material' : 'Routine', sub: f.investigation ? 'Surfaces in active investigation' : 'Standard quarterly evidence', tone: f.investigation ? 'critical' : 'lens', delta: f.investigation ? 'up' : 'flat' },
          ],
        },
        {
          title: 'Path to closure',
          type: 'list',
          items: [
            'Cut the trust relationship or relocate the asset to the matching environment so dev never reaches prod again.',
            'Land the change behind a reviewed PR and tag the Change Calendar entry so the audit packet inherits the rationale.',
            'Refresh the approved baseline — once it matches reality, future drift surfaces automatically.',
          ],
        },
      ],
      actions: [
        ...(f.investigation ? [{ label: `Open ${f.investigation}`, onClick: () => navigate(`/lens/investigations?id=${f.investigation}`), primary: true }] : []),
        { label: 'Open Remediation', onClick: () => navigate('/lens/remediation') },
      ],
    };
  };

  return (
    <div className="space-y-6" data-testid="environment-integrity">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Evidence Engine · Environment Integrity</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Production contamination at a glance</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Score gauge */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col items-center justify-center text-center lg:col-span-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-apex-neutral mb-4">Production Contamination</div>
          <Gauge score={23} />
          <div className="mt-3 text-xs text-apex-success font-mono">↓ 31% from last quarter</div>
          <p className="text-[11px] text-apex-neutral mt-2 max-w-xs leading-relaxed">
            Lower is better. 6 active findings. Production is running non-approved artifacts, configurations, or trust paths.
          </p>
        </div>

        {/* Contamination map */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-apex-ink">Contamination Map</h3>
            <span className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral">3 active edges · auto-updating</span>
          </div>
          <ContaminationMap edges={edges} onEdgeClick={setActiveEdge} />
          {/* Replay scrubber under the IAM edge */}
          {seed.exposure_replay && <ExposureScrubber replay={seed.exposure_replay} />}
        </div>
      </div>

      <HowCalculated storageKey="contamination-score" testId="how-calculated-contamination">
        <p className="mb-2 text-[11px] text-apex-neutral">
          Production Contamination Score measures non-production artifacts,
          configurations, and trust paths present in production accounts.
          Score range: 0 (clean) to 100 (fully contaminated). Lower is better.
        </p>
        <div className="space-y-1.5 font-mono text-[11px] border-t border-slate-100 pt-3">
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Dev-origin images in production</span>
            <span className="text-apex-ink">6 pts · 2 images</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Cross-environment IAM trust paths</span>
            <span className="text-apex-ink">8 pts · 3 paths</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Shared secrets across environments</span>
            <span className="text-apex-ink">4 pts · 1 secret</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-apex-neutral">Dev-style configs in production</span>
            <span className="text-apex-ink">5 pts · 2 resources</span>
          </div>
          <div className="flex justify-between gap-4 border-t border-slate-100 pt-2 mt-1 font-semibold">
            <span className="text-apex-neutral">Current score</span>
            <span className="text-apex-warning">23 / 100</span>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-apex-neutral">
          Updated continuously as CloudTrail events arrive. Every factor traces
          to a specific immutable source record.
        </p>
      </HowCalculated>

      {/* Findings */}
      <div>
        <h3 className="text-sm font-bold text-apex-ink mb-3">6 Contamination Findings</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {seed.environment_findings.map((f) => (
            <motion.div
              key={f.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              data-testid={`env-finding-${f.id}`}
              onClick={() => setActiveFinding(f)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveFinding(f); } }}
              className={`bg-white rounded-lg border p-4 cursor-pointer hover:shadow-sm hover:-translate-y-0.5 transition-all ${f.investigation === 'INV-2026-042' ? 'border-apex-lens ring-1 ring-apex-lens/20' : 'border-slate-200'}`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <h4 className="text-sm font-semibold text-apex-ink leading-snug flex-1">
                  {f.title}
                  {f.investigation === 'INV-2026-042' && (
                    <span className="inline-block align-middle ml-1.5">
                      <InvestigationBadge />
                    </span>
                  )}
                </h4>
                <SeverityBadge severity={f.severity} size="sm" />
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px] text-apex-neutral mb-3">
                <div><span className="text-apex-neutral">Duration: </span><span className="text-apex-ink-2 font-mono">{f.duration_days}d</span></div>
                <div><span className="text-apex-neutral">SLA: </span><span className="text-apex-ink-2 font-mono">{f.sla_days}d</span></div>
                <div className="col-span-2 truncate"><span className="text-apex-neutral">Data impact: </span><span className="text-apex-ink-2">{f.data_impact}</span></div>
                <div className="col-span-2"><span className="text-apex-neutral">Owner: </span><span className="text-apex-ink-2">{f.remediation_owner}</span></div>
              </div>
              <div className="flex items-center justify-between" onClick={(e) => e.stopPropagation()}>
                {f.investigation && (
                  <button onClick={(e) => { e.stopPropagation(); navigate(`/lens/investigations?id=${f.investigation}`); }} className="text-xs font-semibold text-apex-lens">
                    View {f.investigation} →
                  </button>
                )}
                <ActionMenu context={f.id} testId={`env-actions-${f.id}`} />
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Baseline vs Actual */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-apex-ink mb-3">Approved Baseline vs Actual</h3>
        <Tabs defaultValue="split">
          <TabsList>
            <TabsTrigger value="split">Split View</TabsTrigger>
            <TabsTrigger value="baseline">Baseline</TabsTrigger>
            <TabsTrigger value="actual">Actual</TabsTrigger>
          </TabsList>
          <TabsContent value="split">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Column title="Approved Baseline" rows={[
                'IAM trust: identity-prod ⇄ payments-prod only',
                'ECR access: production accounts only',
                'Parameter Store: per-env secrets',
                'Versioning: enabled · Deletion protection: enabled',
                'Console deploys: blocked outside break-glass',
              ]} good />
              <Column title="Actual State" rows={[
                'IAM trust: dev-sandbox → payments-prod (UNAPPROVED)',
                'ECR shared push/pull: dev ↔ prod',
                'Parameter Store: shared dev/prod refs',
                'Versioning: DISABLED · Deletion protection: OFF',
                'Console deploys: 7 in last 30 days',
              ]} drift />
            </div>
          </TabsContent>
          <TabsContent value="baseline">
            <Column title="Approved Baseline" rows={[
              'IAM trust: identity-prod ⇄ payments-prod only',
              'ECR access: production accounts only',
              'Parameter Store: per-env secrets',
            ]} good />
          </TabsContent>
          <TabsContent value="actual">
            <Column title="Actual State" rows={[
              'IAM trust: dev-sandbox → payments-prod (UNAPPROVED)',
              'ECR shared push/pull: dev ↔ prod',
              'Parameter Store: shared dev/prod refs',
            ]} drift />
          </TabsContent>
        </Tabs>
      </div>

      {/* Blast radius */}
      <div className="bg-apex-shell text-white rounded-xl p-5 grain">
        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="w-5 h-5 text-apex-warning shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-bold">Dev Blast Radius</h3>
            <p className="text-xs text-slate-400 mt-1">If stratum-dev-sandbox is compromised today, an attacker can reach:</p>
          </div>
        </div>
        {seed.environment_blast_graph
          ? <BlastRadiusGraph graph={seed.environment_blast_graph} />
          : (
            <ul className="space-y-1.5 text-sm">
              {seed.environment_blast_radius.map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-200">
                  <span className="text-apex-critical mt-0.5">▸</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
      </div>

      {/* Edge evidence dialog */}
      <EdgeEvidenceDialog
        edge={activeEdge}
        open={!!activeEdge}
        onOpenChange={(o) => !o && setActiveEdge(null)}
      />

      {/* Finding drawer */}
      <RecordDrawer
        open={!!activeFinding}
        onOpenChange={(o) => !o && setActiveFinding(null)}
        record={activeFinding ? buildFindingRecord(activeFinding) : null}
        testId="env-finding-drawer"
        onPrev={findingPrev}
        onNext={findingNext}
        position={findingIndex}
        total={findingTotal}
      />
    </div>
  );
}

function Gauge({ score = 23 }) {
  const max = 100;
  // Arc geometry — half-circle from angle 180° (left) to 0° (right)
  // viewBox 220 × 130, center (110, 110), radius 88 — fits cleanly with stroke=14
  const cx = 110;
  const cy = 110;
  const r = 88;
  const startA = Math.PI;            // 180°
  const endA = 0;                    // 0°
  const arcLen = Math.PI * r;        // half-circumference

  const pct = Math.max(0, Math.min(1, score / max));
  const angle = startA + (endA - startA) * pct;
  // Needle position: arc renders above cy (top half), so y = cy - sin*r
  const needleX = cx + Math.cos(angle) * r;
  const needleY = cy - Math.sin(angle) * r;

  const tone =
    score < 20 ? { label: 'GOOD', color: '#10B981', soft: 'rgba(16,185,129,0.10)' }
  : score < 50 ? { label: 'MODERATE', color: '#D97706', soft: 'rgba(217,119,6,0.10)' }
  :              { label: 'CRITICAL', color: '#DC2626', soft: 'rgba(220,38,38,0.10)' };

  // Zone boundaries (as fraction of arc 0..1) for the three segments
  const ZONE = [
    { from: 0.0,  to: 0.20, color: '#10B981' }, // clean
    { from: 0.20, to: 0.50, color: '#F59E0B' }, // moderate
    { from: 0.50, to: 1.00, color: '#DC2626' }, // contaminated
  ];

  // Arc path — top half-circle from west endpoint to east endpoint.
  // SVG with sweep-flag=1, M (cx-r,cy) → A r r 0 0 1 (cx+r,cy) draws the upper half.
  const arcPath = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;

  // Tick marks — at 0, 25, 50, 75, 100
  const ticks = [0, 0.25, 0.5, 0.75, 1.0];

  return (
    <div className="relative w-64" data-testid="contamination-gauge">
      <svg viewBox="0 0 220 150" className="w-full overflow-visible">
        <defs>
          <linearGradient id="gauge-progress" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#10B981" />
            <stop offset="40%" stopColor="#F59E0B" />
            <stop offset="100%" stopColor="#DC2626" />
          </linearGradient>
          <filter id="gauge-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="gauge-track" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F1F5F9" />
            <stop offset="100%" stopColor="#E2E8F0" />
          </linearGradient>
        </defs>

        {/* Zone backgrounds — subtle colored segments behind track */}
        {ZONE.map((z, i) => (
          <path
            key={i}
            d={arcPath}
            fill="none"
            stroke={z.color}
            strokeOpacity="0.10"
            strokeWidth="22"
            strokeLinecap="butt"
            pathLength="1"
            strokeDasharray={`${z.to - z.from} 1`}
            strokeDashoffset={-z.from}
          />
        ))}

        {/* Outer faint guide track */}
        <path
          d={arcPath}
          fill="none"
          stroke="url(#gauge-track)"
          strokeWidth="14"
          strokeLinecap="round"
          pathLength="1"
        />

        {/* Tick marks */}
        {ticks.map((t) => {
          const a = startA + (endA - startA) * t;
          const x1 = cx + Math.cos(a) * (r - 12);
          const y1 = cy - Math.sin(a) * (r - 12);
          const x2 = cx + Math.cos(a) * (r + 6);
          const y2 = cy - Math.sin(a) * (r + 6);
          return (
            <line
              key={t}
              x1={x1} y1={y1} x2={x2} y2={y2}
              stroke="#94A3B8"
              strokeWidth={t === 0 || t === 1 ? 1.4 : 1}
              strokeLinecap="round"
              opacity={t === 0 || t === 1 ? 0.9 : 0.55}
            />
          );
        })}

        {/* Filled progress arc */}
        <motion.path
          d={arcPath}
          fill="none"
          stroke="url(#gauge-progress)"
          strokeWidth="14"
          strokeLinecap="round"
          pathLength="1"
          initial={{ strokeDasharray: '0 1' }}
          animate={{ strokeDasharray: `${pct} 1` }}
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
        />

        {/* Needle marker — outer ring + inner core */}
        <motion.g
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.4 }}
        >
          <circle
            cx={needleX}
            cy={needleY}
            r="9"
            fill={tone.color}
            opacity="0.18"
            filter="url(#gauge-glow)"
          />
          <circle
            cx={needleX}
            cy={needleY}
            r="5.5"
            fill="white"
            stroke={tone.color}
            strokeWidth="2.5"
          />
          <circle cx={needleX} cy={needleY} r="2" fill={tone.color} />
        </motion.g>

        {/* Tick labels (anchored under track) */}
        <text x={cx - r} y={cy + 22} textAnchor="middle" className="fill-slate-400" fontSize="9" fontFamily="ui-monospace, monospace">0</text>
        <text x={cx} y={cy + 22} textAnchor="middle" className="fill-slate-400" fontSize="9" fontFamily="ui-monospace, monospace">50</text>
        <text x={cx + r} y={cy + 22} textAnchor="middle" className="fill-slate-400" fontSize="9" fontFamily="ui-monospace, monospace">100</text>
      </svg>

      {/* Center score block — overlays under arc */}
      <div className="absolute inset-x-0 bottom-3 flex flex-col items-center pointer-events-none">
        <div className="flex items-baseline gap-1">
          <span className="text-5xl font-extrabold tabular tracking-tight leading-none" style={{ color: tone.color }}>
            <AnimatedCounter value={score} />
          </span>
          <span className="text-base font-semibold tabular text-apex-neutral">/100</span>
        </div>
        <span
          className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.16em] font-bold"
          style={{ color: tone.color, backgroundColor: tone.soft }}
        >
          <span className="w-1 h-1 rounded-full" style={{ backgroundColor: tone.color }} />
          {tone.label}
        </span>
      </div>

      {/* Scale legend */}
      <div className="mt-3 flex items-center justify-between text-[9px] uppercase tracking-wider font-mono text-apex-neutral px-1">
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />Clean</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />Moderate</span>
        <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-600" />Contaminated</span>
      </div>
    </div>
  );
}

function ContaminationMap({ edges = [], onEdgeClick }) {
  const [hovered, setHovered] = React.useState(null);
  const sevColor = { critical: '#DC2626', high: '#D97706', medium: '#0EA5E9' };

  // Layout constants — viewBox 800 × 380
  const W = 800;
  const H = 380;
  const NODE_W = 220;
  const NODE_H = 140;
  const leftX = 24;
  const rightX = W - NODE_W - 24;
  const nodeY = (H - NODE_H) / 2;
  const sourceAnchor = { x: leftX + NODE_W, y: nodeY + NODE_H / 2 };
  const targetAnchor = { x: rightX, y: nodeY + NODE_H / 2 };

  return (
    <div data-testid="contamination-map" className="relative bg-gradient-to-br from-slate-50 via-white to-slate-50 rounded-xl border border-slate-200 overflow-hidden">
      {/* Subtle grid overlay */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
        <defs>
          <pattern id="contamGrid" width="24" height="24" patternUnits="userSpaceOnUse">
            <path d="M 24 0 L 0 0 0 24" fill="none" stroke="#E2E8F0" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#contamGrid)" />
      </svg>

      <div className="relative w-full">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto"
          role="img"
          aria-label="Contamination map between dev and production environments"
        >
          <defs>
            {/* Gradients per severity for stroke */}
            {Object.entries(sevColor).map(([k, c]) => (
              <linearGradient key={k} id={`edge-${k}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={c} stopOpacity="0.15" />
                <stop offset="100%" stopColor={c} stopOpacity="0.95" />
              </linearGradient>
            ))}
            {/* Glow filter */}
            <filter id="nodeGlow" x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="6" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            {/* Arrowhead markers per severity */}
            {Object.entries(sevColor).map(([k, c]) => (
              <marker
                key={`arr-${k}`}
                id={`arrow-${k}`}
                viewBox="0 0 12 12"
                refX="10" refY="6"
                markerUnits="strokeWidth"
                markerWidth="6" markerHeight="6"
                orient="auto"
              >
                <path d="M 0 0 L 12 6 L 0 12 z" fill={c} />
              </marker>
            ))}
          </defs>

          {/* SOURCE node — DEV */}
          <NodeBlock
            x={leftX} y={nodeY} w={NODE_W} h={NODE_H}
            tone="slate"
            label="DEV"
            name="stratum-dev-sandbox"
            accountId="492817330142"
            stats={[
              { k: 'Risk class', v: 'Non-prod' },
              { k: 'Owner', v: 'Platform' },
              { k: 'Approved', v: 'no prod scope' },
            ]}
          />

          {/* TARGET node — PROD */}
          <NodeBlock
            x={rightX} y={nodeY} w={NODE_W} h={NODE_H}
            tone="lens"
            label="PROD"
            name="stratum-payments-prod"
            accountId="918273645012"
            stats={[
              { k: 'Risk class', v: 'PCI · Tier 0' },
              { k: 'Owner', v: 'Payments' },
              { k: 'Data', v: 'Cardholder DB' },
            ]}
          />

          {/* Edges — bezier paths between source and target */}
          {edges.map((e, i) => {
            const stride = NODE_H / (edges.length + 1);
            const sy = nodeY + stride * (i + 1);
            const ty = sy; // straight horizontal mid-line per row
            const sx = sourceAnchor.x;
            const tx = targetAnchor.x;
            const cp1x = sx + (tx - sx) * 0.35;
            const cp2x = tx - (tx - sx) * 0.35;
            const d = `M ${sx} ${sy} C ${cp1x} ${sy}, ${cp2x} ${ty}, ${tx} ${ty}`;
            const c = sevColor[e.severity];
            const isCritical = e.severity === 'critical';
            const isHovered = hovered === e.id;
            const dim = hovered && hovered !== e.id;

            return (
              <g
                key={e.id}
                data-testid={`map-edge-${e.id}`}
                onMouseEnter={() => setHovered(e.id)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => onEdgeClick?.(e)}
                style={{ cursor: 'pointer', opacity: dim ? 0.35 : 1, transition: 'opacity 0.2s' }}
              >
                {/* Glow layer for critical/hovered */}
                {(isCritical || isHovered) && (
                  <path
                    d={d}
                    fill="none"
                    stroke={c}
                    strokeOpacity="0.25"
                    strokeWidth={isHovered ? 12 : 9}
                    strokeLinecap="round"
                    style={{ filter: 'blur(4px)' }}
                  />
                )}
                {/* Faint underlay */}
                <path d={d} fill="none" stroke={c} strokeOpacity="0.18" strokeWidth="2.5" />
                {/* Animated dashed flow */}
                <motion.path
                  d={d}
                  fill="none"
                  stroke={`url(#edge-${e.severity})`}
                  strokeWidth={isHovered ? 3 : 2.2}
                  strokeDasharray="6 6"
                  strokeLinecap="round"
                  markerEnd={`url(#arrow-${e.severity})`}
                  initial={{ strokeDashoffset: 0 }}
                  animate={{ strokeDashoffset: -24 }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
                />
                {/* Severity dot at midpoint */}
                <circle cx={(sx + tx) / 2} cy={sy} r={isHovered ? 5.5 : 4} fill={c} stroke="white" strokeWidth="2">
                  {isCritical && (
                    <animate attributeName="r" values="4;6;4" dur="1.6s" repeatCount="indefinite" />
                  )}
                </circle>
                {/* Edge label chip — anchored above midpoint */}
                <g transform={`translate(${(sx + tx) / 2}, ${sy})`}>
                  <foreignObject x="-110" y="-46" width="220" height="36">
                    <div
                      className="bg-white border rounded-md shadow-sm px-2 py-1 text-center mx-auto w-fit max-w-full"
                      style={{ borderColor: `${c}80` }}
                    >
                      <div className="flex items-center justify-center gap-1.5">
                        <span className="text-[10.5px] font-semibold text-apex-ink leading-none">{e.label}</span>
                        <span className="text-[8.5px] uppercase tracking-wider font-bold leading-none" style={{ color: c }}>
                          {e.severity}
                        </span>
                      </div>
                      <div className="text-[9px] font-mono text-apex-neutral leading-none mt-0.5 truncate">{e.meta}</div>
                    </div>
                  </foreignObject>
                </g>
              </g>
            );
          })}
        </svg>

        {/* Hidden a11y / e2e buttons (one per edge) — same data-testid as before so existing tests keep passing */}
        <div className="sr-only">
          {edges.map((e) => (
            <button
              key={e.id}
              type="button"
              data-testid={`map-edge-button-${e.id}`}
              onClick={() => onEdgeClick?.(e)}
            >
              {e.label}
            </button>
          ))}
        </div>
      </div>

      {/* Footer legend */}
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-t border-slate-200 bg-white/80">
        <div className="flex flex-wrap items-center gap-3 text-[10px] font-mono text-apex-neutral">
          <LegendDot color="#DC2626" label={`Critical · ${edges.filter(e => e.severity === 'critical').length}`} />
          <LegendDot color="#D97706" label={`High · ${edges.filter(e => e.severity === 'high').length}`} />
          <LegendDot color="#0EA5E9" label={`Medium · ${edges.filter(e => e.severity === 'medium').length}`} />
        </div>
        <span className="text-[10px] font-mono text-apex-neutral">Hover to isolate · click for evidence</span>
      </div>
    </div>
  );
}

function NodeBlock({ x, y, w, h, tone, label, name, accountId, stats }) {
  const isProd = tone === 'lens';
  const fill = isProd ? '#0F172A' : '#F8FAFC';
  const border = isProd ? '#0D9488' : '#CBD5E1';
  const labelColor = isProd ? '#0D9488' : '#64748B';
  const nameColor = isProd ? '#FFFFFF' : '#0F172A';
  const accountColor = isProd ? '#94A3B8' : '#64748B';
  const statKColor = isProd ? '#94A3B8' : '#64748B';
  const statVColor = isProd ? '#E2E8F0' : '#334155';

  return (
    <g>
      {/* Node body */}
      <rect
        x={x} y={y} width={w} height={h} rx="14"
        fill={fill}
        stroke={border}
        strokeWidth="1.5"
        filter={isProd ? 'url(#nodeGlow)' : undefined}
      />
      {isProd && (
        <rect x={x} y={y} width={w} height={h} rx="14" fill="none" stroke={border} strokeOpacity="0.3" strokeWidth="3" />
      )}
      {/* Header row */}
      <text x={x + 14} y={y + 22} fontSize="10" fontWeight="700" fontFamily="ui-monospace, SFMono-Regular, monospace" fill={labelColor} letterSpacing="2">
        {label}
      </text>
      <circle
        cx={x + w - 16} cy={y + 18} r={isProd ? 4 : 3}
        fill={isProd ? '#10B981' : '#94A3B8'}
      >
        {isProd && <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />}
      </circle>
      {/* Name */}
      <text x={x + 14} y={y + 44} fontSize="13" fontWeight="600" fontFamily="ui-monospace, SFMono-Regular, monospace" fill={nameColor}>
        {name}
      </text>
      {/* Account ID */}
      <text x={x + 14} y={y + 60} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, monospace" fill={accountColor}>
        aws · {accountId}
      </text>
      {/* Divider */}
      <line x1={x + 14} y1={y + 70} x2={x + w - 14} y2={y + 70} stroke={border} strokeOpacity="0.4" strokeWidth="0.75" />
      {/* Stats */}
      {stats.map((s, i) => (
        <g key={s.k}>
          <text x={x + 14} y={y + 88 + i * 14} fontSize="10" fontFamily="ui-monospace, SFMono-Regular, monospace" fill={statKColor}>
            {s.k}
          </text>
          <text x={x + w - 14} y={y + 88 + i * 14} fontSize="10" textAnchor="end" fontFamily="ui-monospace, SFMono-Regular, monospace" fill={statVColor}>
            {s.v}
          </text>
        </g>
      ))}
    </g>
  );
}

function EnvNode({ label, name, accountId, tone = 'slate', stats = [] }) {
  const isProd = tone === 'lens';
  return (
    <div
      className={`relative rounded-xl border p-4 ${
        isProd
          ? 'bg-apex-shell text-white border-apex-lens shadow-[0_0_0_1px_rgba(13,148,136,0.15)]'
          : 'bg-slate-50 border-slate-200 text-apex-ink'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isProd ? 'text-apex-lens' : 'text-apex-neutral'}`}>{label}</span>
        <span className={`w-2 h-2 rounded-full ${isProd ? 'bg-apex-lens' : 'bg-slate-400'} ${isProd ? 'shadow-[0_0_8px_rgba(13,148,136,0.6)]' : ''}`} />
      </div>
      <div className={`font-mono text-[12px] font-semibold leading-tight ${isProd ? 'text-white' : 'text-apex-ink'}`}>{name}</div>
      <div className={`font-mono text-[10px] mt-0.5 ${isProd ? 'text-slate-400' : 'text-apex-neutral'}`}>aws · {accountId}</div>
      <div className={`mt-3 pt-3 border-t ${isProd ? 'border-slate-700' : 'border-slate-200'} space-y-1`}>
        {stats.map((s) => (
          <div key={s.k} className="flex justify-between text-[10px] font-mono">
            <span className={isProd ? 'text-slate-400' : 'text-apex-neutral'}>{s.k}</span>
            <span className={isProd ? 'text-slate-200' : 'text-apex-ink-2'}>{s.v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Edge({ edge, color, delay = 0, onClick }) {
  return (
    <div className="relative my-2" data-testid={`map-edge-${edge.id}`}>
      {/* Connector line — animated dashed */}
      <svg viewBox="0 0 100 8" preserveAspectRatio="none" className="w-full h-2 absolute left-0 top-1/2 -translate-y-1/2 -z-0">
        <line x1="0" y1="4" x2="100" y2="4" stroke={color} strokeOpacity="0.2" strokeWidth="2" />
        <motion.line
          x1="0" y1="4" x2="100" y2="4"
          stroke={color} strokeWidth="2" strokeDasharray="4 4"
          initial={{ strokeDashoffset: 0 }}
          animate={{ strokeDashoffset: -16 }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'linear', delay }}
        />
      </svg>
      {/* Lane chip — interactive */}
      <motion.button
        type="button"
        onClick={onClick}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ y: -1, boxShadow: '0 4px 14px rgba(15,23,42,0.08)' }}
        whileTap={{ scale: 0.98 }}
        transition={{ delay, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        data-testid={`map-edge-button-${edge.id}`}
        className="relative z-10 mx-auto w-fit max-w-full bg-white border rounded-md px-2.5 py-1 shadow-sm text-left cursor-pointer hover:bg-slate-50 transition-colors"
        style={{ borderColor: `${color}80` }}
      >
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <span className="text-[11px] font-semibold text-apex-ink leading-tight whitespace-nowrap">{edge.label}</span>
          <span className="text-[10px] uppercase tracking-wider font-bold leading-tight whitespace-nowrap" style={{ color }}>
            {edge.severity}
          </span>
        </div>
        <div className="text-[10px] font-mono text-apex-neutral leading-tight mt-0.5 truncate">{edge.meta}</div>
      </motion.button>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

function Column({ title, rows = [], good = false, drift = false }) {
  return (
    <div className={`rounded-lg p-4 ${good ? 'bg-emerald-50 border border-emerald-200' : drift ? 'bg-red-50 border border-red-200' : 'bg-slate-50 border border-slate-200'}`}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold mb-2 ${good ? 'text-apex-success' : drift ? 'text-apex-critical' : 'text-apex-neutral'}`}>{title}</div>
      <ul className="space-y-1.5 text-xs">
        {rows.map((r, i) => (
          <li key={i} className={`font-mono leading-snug ${drift ? 'text-apex-critical' : 'text-apex-ink-2'}`}>{r}</li>
        ))}
      </ul>
    </div>
  );
}
